import React, { createContext, useContext, useEffect, useState } from 'react';

interface ABTest {
  testId: string;
  variant: 'A' | 'B';
  timestamp: number;
}

interface ABTestingContextType {
  getVariant: (testId: string) => 'A' | 'B';
  trackConversion: (testId: string, conversionType: string) => void;
}

const ABTestingContext = createContext<ABTestingContextType | undefined>(undefined);

export const useABTesting = () => {
  const context = useContext(ABTestingContext);
  if (!context) {
    throw new Error('useABTesting must be used within ABTestingProvider');
  }
  return context;
};

export const ABTestingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tests, setTests] = useState<Record<string, ABTest>>({});

  useEffect(() => {
    // Load existing tests from localStorage
    const stored = localStorage.getItem('ab_tests');
    if (stored) {
      setTests(JSON.parse(stored));
    }
  }, []);

  const getVariant = (testId: string): 'A' | 'B' => {
    if (tests[testId]) {
      return tests[testId].variant;
    }

    // Assign new variant (50/50 split)
    const variant = Math.random() < 0.5 ? 'A' : 'B';
    const newTest: ABTest = {
      testId,
      variant,
      timestamp: Date.now(),
    };

    const updatedTests = { ...tests, [testId]: newTest };
    setTests(updatedTests);
    localStorage.setItem('ab_tests', JSON.stringify(updatedTests));

    return variant;
  };

  const trackConversion = (testId: string, conversionType: string) => {
    const test = tests[testId];
    if (!test) return;

    // Track conversion in localStorage
    const conversions = JSON.parse(localStorage.getItem('ab_conversions') || '[]');
    conversions.push({
      testId,
      variant: test.variant,
      conversionType,
      timestamp: Date.now(),
    });
    localStorage.setItem('ab_conversions', JSON.stringify(conversions));

    // Also track in analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'ab_test_conversion', {
        test_id: testId,
        variant: test.variant,
        conversion_type: conversionType,
      });
    }
  };

  return (
    <ABTestingContext.Provider value={{ getVariant, trackConversion }}>
      {children}
    </ABTestingContext.Provider>
  );
};
